import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { PaymentOrderDescriptionResponse } from 'src/app/models/payment-order-description.model';
import { environment } from 'src/environments/environment';
import { Transaction } from '../../models/transaction.model';
import { BaseService } from '../base/base.service';
import { X_OTP } from './../../app.constants';
import { PaymentInternalCreate } from './../../models/payment-internal.model';
import { PaymentOrderCancelRequest } from './../../models/payment-order-cancel-request.model';
import { PaymentOrderCreate } from './../../models/payment-order-create.model';
import { TemplatePaymentOrder } from './../../models/template-payment-order.model';
import { createHttpParams } from './../../shared/utils/create-http-params.utils';

@Injectable({
  providedIn: 'root'
})
export class TransactionService extends BaseService<Transaction> {
  environment = environment;
  private stopPolling$ = new Subject<void>();

  constructor(protected http: HttpClient) {
    super(http, 'transactions');
  }

  orderList(searchValues: {
    iban: string | null;
    currency?: string | null;
  }): Observable<HttpResponse<{ data: Transaction[] }>> {
    const params = createHttpParams(searchValues);

    return this.http.get<{ data: Transaction[] }>(
      `${this.resourceUrl}/orders`,
      {
        params,
        observe: 'response'
      }
    );
  }

  eBillsList(searchValues: any): Observable<any> {
    const params = createHttpParams(searchValues);
    return this.http.get<{ data: any[] }>(
      `${this.environment.baseUrl}/e-bills`,
      {
        params,
        observe: 'response'
      }
    );
  }

  downloadTransactionDetailsPDF(
    transactionId: string,
    confirmationTransactionId?: string
  ): Observable<HttpResponse<any>> {
    const params = createHttpParams({ confirmationTransactionId });
    return this.http.get<{ data: string }>(
      `${this.resourceUrl}/${transactionId}/pdf-export`,
      {
        params,
        observe: 'response'
      }
    );
  }

  cancelPaymentOrders(
    requests: [PaymentOrderCancelRequest]
  ): Observable<HttpResponse<any>> {
    let body = {
      requests
    };
    return this.http.post<any>(`${this.resourceUrl}/orders/cancel`, body, {
      observe: 'response'
    });
  }

  getPaymentOrderDescriptions(
    body: Transaction
  ): Observable<HttpResponse<PaymentOrderDescriptionResponse>> {
    return this.http.post<PaymentOrderDescriptionResponse>(
      `${this.resourceUrl}/descriptions`,
      body,
      {
        observe: 'response'
      }
    );
  }

  getTransaction(
    body: Transaction
  ): Observable<HttpResponse<PaymentOrderDescriptionResponse>> {
    return this.http.post<PaymentOrderDescriptionResponse>(
      `${this.resourceUrl}/descriptions`,
      body,
      {
        observe: 'response'
      }
    );
  }

  sendTransactionViaEmail(
    transactionId: string
  ): Observable<HttpResponse<{ data: string }>> {
    return this.http.post<{ data: string }>(
      `${this.resourceUrl}/${transactionId}/email`,
      null,
      {
        observe: 'response'
      }
    );
  }

  createPaymentOrder(
    entity: PaymentOrderCreate
  ): Observable<HttpResponse<PaymentOrderCreate>> {
    return this.http.post<PaymentOrderCreate>(`${this.resourceUrl}`, entity, {
      observe: 'response'
    });
  }

  updatePaymentOrder(
    transactionId: string,
    entity: Transaction,
    accountId: string
  ): Observable<HttpResponse<Transaction>> {
    return this.http.put<Transaction>(
      `${this.resourceUrl}/${transactionId}/${accountId}`,
      entity,
      {
        observe: 'response'
      }
    );
  }

  signBatchPayment(body: TemplatePaymentOrder): Observable<HttpResponse<null>> {
    const headers = new HttpHeaders().set(X_OTP, '1234');
    return this.http.post<null>(`${this.resourceUrl}/batch-sign`, body, {
      headers,
      observe: 'response'
    });
  }

  createBatchPayment(
    body: TemplatePaymentOrder
  ): Observable<HttpResponse<{ data: PaymentOrderCreate }>> {
    return this.http.post<{ data: PaymentOrderCreate }>(
      `${this.resourceUrl}/init-batch`,
      body,
      {
        observe: 'response'
      }
    );
  }

  createInternalOrder(
    body: PaymentInternalCreate
  ): Observable<HttpResponse<PaymentOrderCreate>> {
    return this.http.post<PaymentOrderCreate>(
      `${this.resourceUrl}/internal`,
      body,
      {
        observe: 'response'
      }
    );
  }

  exportTransaction(
    searchValues: any
  ): Observable<HttpResponse<{ data: string }>> {
    const params = createHttpParams(searchValues);
    return this.http.get<{ data: string }>(`${this.resourceUrl}/export`, {
      params,
      observe: 'response'
    });
  }

  authorizeSignPaymentOrder(
    entity: PaymentOrderCreate,
    data: string,
    accountId: any
  ): Observable<HttpResponse<any>> {
    const headers = new HttpHeaders().set(X_OTP, data);
    return this.http.post<any>(
      `${this.resourceUrl}/authorize-sign/${accountId}`,
      entity,
      {
        headers,
        observe: 'response'
      }
    );
  }

  signPaymentOrder(
    entity: PaymentOrderCreate,
    data: string,
    accountId: any
  ): Observable<HttpResponse<any>> {
    const headers = new HttpHeaders().set(X_OTP, data);
    return this.http.post<any>(
      `${this.resourceUrl}/sign/${accountId}`,
      entity,
      {
        headers,
        observe: 'response'
      }
    );
  }

  continuePolling(
    pollInterval: any,
    response: any,
    data: string,
    accountId: any
  ): Observable<any> {
    return interval(2000, pollInterval).pipe(
      switchMap(() => this.signPaymentOrder(response, data, accountId)),
      takeUntil(this.stopPolling$)
    );
  }
}
